import type { EntryCollection, EntrySkeletonType } from 'contentful'

export const usePreviewProductDetailsPageFilter = () => {
  const { getPanelInfo } = useScheduledActions()

  const filterByPreviewId = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => {
    if (!previewConfig.previewId) return entries

    // Find the pdpPreferences entry with matching sys.id
    const pdpPreferencesItem = entries.items.find(
      (item) => item.sys.contentType.sys.id === 'pdpPreferences' && item.sys.id === previewConfig.previewId,
    )

    if (!pdpPreferencesItem) return entries

    // Return only this entry in the items array
    return {
      ...entries,
      items: pdpPreferencesItem ? [pdpPreferencesItem] : [],
    }
  }

  const filterArrayFieldByTag = (items, tag: string) =>
    items.filter((item) => hasFields(item) && hasPersonalizationTag(item, tag))

  const filterNestedPanels = (panels, tag: string) => {
    return panels.map((panel) => {
      if (!hasFields(panel) || !Array.isArray(panel.fields?.panels)) return panel
      return {
        ...panel,
        fields: {
          ...panel.fields,
          panels: filterArrayFieldByTag(panel.fields.panels, tag),
        },
      }
    })
  }

  const filterByPersonalizationTag = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => {
    const { previewPersonalizationTag: tag } = previewConfig

    // First filter by previewId if provided
    const filteredEntries = filterByPreviewId(entries, previewConfig)

    const pdpPreferencesItem = filteredEntries.items.length > 0 ? filteredEntries.items[0] : null
    if (pdpPreferencesItem) {
      pdpPreferencesItem.fields = processNestedFieldsByTag(
        pdpPreferencesItem.fields as typeof pdpPreferencesItem.fields,
        tag,
      ) as typeof pdpPreferencesItem.fields
    }

    return filteredEntries
  }

  const findPdpPreferencesItem = (entries: EntryCollection<EntrySkeletonType, undefined, string>, locale: string) =>
    entries.items.find((item) => item.sys.contentType.sys.id === 'pdpPreferences' && item.fields.locale === locale)

  const getAllPanels = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ): PanelInfo[] => {
    const pdpPreferencesItem = findPdpPreferencesItem(entries, previewConfig.previewLocale)
    if (!pdpPreferencesItem) return []

    const extractedPanels = new Set<string>()
    const panels: PanelInfo[] = []

    const extractPanelsFromField = (fieldName: string, fieldValue): void => {
      // Handle arrays
      if (Array.isArray(fieldValue)) {
        fieldValue.forEach((item) => {
          // Add the item itself if it has sys.id
          if (item?.sys?.id && !extractedPanels.has(item.sys.id)) {
            extractedPanels.add(item.sys.id)
            const panel = extractPanelFromValue(item)
            if (panel) panels.push(getPanelInfo(panel))
          }

          // Recursively extract from nested fields
          if (item?.fields && typeof item.fields === 'object') {
            Object.entries(item.fields).forEach(([nestedFieldName, nestedFieldValue]) => {
              extractPanelsFromField(nestedFieldName, nestedFieldValue)
            })
          }
        })
        return
      }

      // Handle single object with sys.id
      if (fieldValue && typeof fieldValue === 'object' && fieldValue?.sys?.id) {
        if (!extractedPanels.has(fieldValue.sys.id)) {
          extractedPanels.add(fieldValue.sys.id)
          const panel = extractPanelFromValue(fieldValue)
          if (panel) panels.push(getPanelInfo(panel))
        }

        // Recursively extract from nested fields
        if (fieldValue?.fields && typeof fieldValue.fields === 'object') {
          Object.entries(fieldValue.fields).forEach(([nestedFieldName, nestedFieldValue]) => {
            extractPanelsFromField(nestedFieldName, nestedFieldValue)
          })
        }
      }
    }

    Object.entries(pdpPreferencesItem.fields).forEach(([fieldName, fieldValue]) => {
      extractPanelsFromField(fieldName, fieldValue)
    })

    return panels
  }

  const hasNestedPanels = (items): boolean => {
    return (
      Array.isArray(items) &&
      items.length > 0 &&
      typeof items[0] === 'object' &&
      'fields' in items[0] &&
      Array.isArray(items[0].fields?.panels)
    )
  }

  const filterNestedPanelsForDisplayInfo = (items, panelDisplayInfo: Record<string, { show: boolean | string }>) => {
    return items.map((panel) => {
      if (hasFields(panel) && Array.isArray(panel.fields?.panels)) {
        return {
          ...panel,
          fields: {
            ...panel.fields,
            panels: filterArrayByDisplayInfo(panel.fields.panels, panelDisplayInfo),
          },
        }
      }
      return panel
    })
  }

  const removePanelsForDisplayInfo = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    panelDisplayInfo: Record<string, { show: boolean | string }>,
    previewConfig: PreviewConfig,
  ) => {
    const locale = previewConfig.previewLocale || 'en-AU'
    const pdpPreferencesItem = findPdpPreferencesItem(entries, locale)
    if (!pdpPreferencesItem) return entries

    Object.entries(pdpPreferencesItem.fields).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        let filtered = filterArrayByDisplayInfo(value, panelDisplayInfo)

        // Generic: Handle any field with nested panels structure
        if (hasNestedPanels(filtered)) {
          filtered = filterNestedPanelsForDisplayInfo(filtered, panelDisplayInfo)
        }

        pdpPreferencesItem.fields[key] = filtered as typeof value
      } else if (hasFields(value)) {
        const itemId = extractPanelId(value)
        if (itemId && !shouldKeepPanel(itemId, panelDisplayInfo)) {
          delete pdpPreferencesItem.fields[key]
        }
      }
    })

    return entries
  }

  const filterByMarket = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => filterEntriesByMarket(entries, previewConfig.previewMarket as string)

  const filterByPreviewDate = async (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => filterByPreviewDateOrchestration(entries, previewConfig, getAllPanels, removePanelsForDisplayInfo)

  return {
    filterByPreviewId,
    filterByPersonalizationTag,
    filterByMarket,
    filterByPreviewDate,
    getAllPanels,
  }
}
